// src/admin/pages/customers/CustomersManagement.tsx
import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  Pagination,
  InputAdornment,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Diversity3 as CustomersIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ShoppingBag as ShoppingBagIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { Customer, CustomerOrderStats, CustomerService } from './customerService';
import { useAuth } from '../../../contexts/AuthContext';
import { useCustomers, useCustomerMutations } from '../../../hooks/useCustomers';
import ToastNotification from '../common/Toastnotification';

const CUSTOMERS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Chip style helper (same palette used across other list pages)
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };

  const lightPalette: Record<string, PaletteEntry> = {
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242' },
  };
  const darkPalette: Record<string, PaletteEntry> = {
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// CustomersManagement
// ─────────────────────────────────────────────────────────────────────────────
const CustomersManagement: React.FC = () => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canView = isSuperAdmin || hasPermission('customers.view');
  const canToggleStatus = isSuperAdmin || hasPermission('customers.toggle_status');

  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const [viewOpen, setViewOpen] = useState(false);
  const [viewedCustomer, setViewedCustomer] = useState<Customer | null>(null);
  const [orderStats, setOrderStats] = useState<CustomerOrderStats | null>(null);
  const [orderStatsLoading, setOrderStatsLoading] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { data: customers = [], isLoading, isFetching, isError, error } = useCustomers();
  const { toggleStatus } = useCustomerMutations();

  const loading = isLoading || isFetching;
  const isPermissionError = isError && (error as Error)?.message?.includes('permission');

  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  // ── Stats (from the full unfiltered list) ──────────────────────────────────
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter((c) => c.isActive).length;
  const inactiveCustomers = totalCustomers - activeCustomers;

  // ── Search + status filter (client-side — see customerService for why) ────
  const filteredCustomers = useMemo(() => {
    let result = customers;
    if (statusFilter !== 'all') {
      const wantActive = statusFilter === 'active';
      result = result.filter((c) => c.isActive === wantActive);
    }
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          (c.phone && c.phone.toLowerCase().includes(q)) ||
          c.uid.toLowerCase().includes(q)
      );
    }
    return result;
  }, [customers, searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE));
  const pagedCustomers = filteredCustomers.slice(
    (currentPage - 1) * CUSTOMERS_PER_PAGE,
    currentPage * CUSTOMERS_PER_PAGE
  );

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  const resetToFirstPage = () => setCurrentPage(1);
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => setCurrentPage(page);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
  };

  // ── Toggle status ───────────────────────────────────────────────────────────
  const handleToggleStatus = async (customer: Customer) => {
    if (!canToggleStatus) return;
    try {
      const newStatus = !customer.isActive;
      await toggleStatus.mutateAsync({ uid: customer.uid, isActive: newStatus });
      showToast(
        `${customer.name || customer.email} ${newStatus ? 'can now log in' : 'login has been disabled'}`,
        'success'
      );
    } catch (err: any) {
      showToast(`Failed to update customer status: ${err.message || 'Unknown error'}`, 'error');
    }
  };

  // ── View drawer ──────────────────────────────────────────────────────────────
  const handleView = async (customer: Customer) => {
    setViewedCustomer(customer);
    setViewOpen(true);
    setOrderStats(null);
    setOrderStatsLoading(true);
    try {
      const stats = await CustomerService.getCustomerOrderStats(customer.email);
      setOrderStats(stats);
    } finally {
      setOrderStatsLoading(false);
    }
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewedCustomer(null);
    setOrderStats(null);
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CustomersIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              You don't have permission to view customers. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isLoading && customers.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading customers...
        </Typography>
      </Box>
    );
  }

  if (isPermissionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CustomersIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              {(error as Error)?.message}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <CustomersIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Customers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shoppers registered on the storefront — disable a customer to block them from logging in
            </Typography>
          </Box>
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={4} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Total Customers
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' } }}>
                  {totalCustomers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={4} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Active
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' } }}>
                  {activeCustomers}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {totalCustomers > 0 ? `${((activeCustomers / totalCustomers) * 100).toFixed(1)}% of total` : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={12} md={4} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Disabled (Login Blocked)
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' } }}>
                  {inactiveCustomers}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {totalCustomers > 0 ? `${((inactiveCustomers / totalCustomers) * 100).toFixed(1)}% of total` : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by name, email, phone... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)} label="Status">
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="inactive">Disabled Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${filteredCustomers.length} customers matching your filters`
                        : `Showing ${filteredCustomers.length} customers`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
        {filteredCustomers.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <CustomersIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Customers Match Your Filters' : 'No Customers Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No shoppers have registered on the storefront yet.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {filteredCustomers.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{ flex: 1, overflow: 'auto', maxHeight: { md: '100%' }, mb: 2, '& .MuiTable-root': { minWidth: { xs: 480, md: 760 } } }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Phone</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Registered</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedCustomers.map((customer) => (
                    <TableRow key={customer.uid} sx={{ '&:hover': { backgroundColor: 'action.hover' }, '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar src={customer.photoURL || undefined} alt={customer.name || customer.email} sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, flexShrink: 0 }}>
                            {(customer.name || customer.email || '?')[0].toUpperCase()}
                          </Avatar>
                          <Box minWidth={0}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} noWrap>
                              {customer.name || 'No Name'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }} noWrap>
                              {customer.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                          {customer.email}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {customer.phone || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={customer.isActive ? 'Active' : 'Disabled'}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(customer.isActive ? 'success' : 'default', isDark)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                          <Tooltip title="View Details">
                            <IconButton onClick={() => handleView(customer)} size="small" color="primary">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {canToggleStatus && (
                            <Tooltip title={customer.isActive ? 'Disable Login' : 'Enable Login'}>
                              <IconButton onClick={() => handleToggleStatus(customer)} size="small" color={customer.isActive ? 'error' : 'success'}>
                                {customer.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing{' '}
                    {filteredCustomers.length === 0 ? 0 : (currentPage - 1) * CUSTOMERS_PER_PAGE + 1}–
                    {Math.min(currentPage * CUSTOMERS_PER_PAGE, filteredCustomers.length)} of {filteredCustomers.length} customers
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {totalPages}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── View Drawer ── */}
      <Drawer anchor="right" open={viewOpen} onClose={handleCloseView} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CustomersIcon color="primary" />
              <Typography variant="h6">Customer Details</Typography>
            </Stack>
            <IconButton onClick={handleCloseView} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {viewedCustomer && (
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar src={viewedCustomer.photoURL || undefined} sx={{ width: 64, height: 64 }}>
                    {(viewedCustomer.name || viewedCustomer.email || '?')[0].toUpperCase()}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography variant="h6" noWrap>{viewedCustomer.name || 'No Name'}</Typography>
                    <Chip
                      label={viewedCustomer.isActive ? 'Active' : 'Login Disabled'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(viewedCustomer.isActive ? 'success' : 'default', isDark)}
                    />
                  </Box>
                </Stack>

                <Divider />

                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{viewedCustomer.email}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{viewedCustomer.phone || 'Not provided'}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Registered {viewedCustomer.createdAt ? new Date(viewedCustomer.createdAt).toLocaleDateString() : '—'}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <ShoppingBagIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2">Order History</Typography>
                  </Stack>
                  {orderStatsLoading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h6">{orderStats?.orderCount ?? 0}</Typography>
                        <Typography variant="caption" color="text.secondary">Total Orders</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          {(orderStats?.totalSpent ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Total Spent</Typography>
                      </Grid>
                    </Grid>
                  )}
                </Box>

                {canToggleStatus && (
                  <>
                    <Divider />
                    <Button
                      variant="outlined"
                      color={viewedCustomer.isActive ? 'error' : 'success'}
                      startIcon={viewedCustomer.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      onClick={async () => {
                        await handleToggleStatus(viewedCustomer);
                        setViewedCustomer({ ...viewedCustomer, isActive: !viewedCustomer.isActive });
                      }}
                    >
                      {viewedCustomer.isActive ? 'Disable Login' : 'Enable Login'}
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Drawer>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default CustomersManagement;
